import React, { useState, useEffect, RefObject } from 'react';
import { toast } from 'react-toastify';
import { HTMLText } from '@morressier/ts';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { FaDownload, FaCopy, FaChevronLeft } from 'react-icons/fa';
import CopyToClipboard from 'react-copy-to-clipboard';
import { logEvent } from 'utils/eventTracking';
import { saveAs } from 'file-saver';

import * as ContainerStyles from 'containers/styles';
import styled from 'styled-components';
import { useRegAndTicketingContext } from 'store/modules/eventTicketing';
import { FeatureFlags } from 'config/featureFlags';
import * as Styles from './styles';

export const BREAKPOINT_XXSMALL = 0;
export const BREAKPOINT_XSMALL = 401;
export const BREAKPOINT_SMALL = 541;
export const BREAKPOINT_MEDIUM = 721;
export const BREAKPOINT_LARGE = 901;
export const BREAKPOINT_XLARGE = 1025;

const Backbutton = styled(ContainerStyles.Backbutton)`
  display: flex;
  align-items: 'center';

  && {
    margin-right: unset;
    margin-top: unset;
    margin-bottom: unset;
  }
`;
interface TopSectionProps {
  isAuthenticated: boolean;
  title: string;
  titleRef: RefObject<HTMLDivElement>;
  doiLink?: string;
  pdfUrl?: string;
  posterId?: string;
  onBackButtonClick?: () => void;
}

const defaultProps = {
  doiLink: '',
  pdfUrl: '',
  posterId: '',
};

const TopSection: React.FC<TopSectionProps> = props => {
  const {
    isAuthenticated,
    title,
    titleRef,
    doiLink,
    pdfUrl,
    posterId,
    onBackButtonClick = () => null,
  } = props;

  const [showTitle, setShowTitle] = useState(false);
  const [hasScrollStarted, setHasScrollStarted] = useState(false);

  const onScroll = () => {
    if (window.pageYOffset > 1) {
      setHasScrollStarted(true);
    } else {
      setHasScrollStarted(false);
    }

    if (titleRef.current && window.pageYOffset + 64 > titleRef.current.offsetTop) {
      setShowTitle(true);
    } else {
      setShowTitle(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  });

  const isDesktop = useMediaQuery(`(min-width:${BREAKPOINT_MEDIUM}px)`);
  const showBackText = isDesktop || !showTitle;

  const { state, processAction } = useRegAndTicketingContext();

  const isTicketingFlagEnabled = FeatureFlags.Event_Ticketing.isEnabled();

  const downloadProps = () => {
    if (isTicketingFlagEnabled) {
      return {
        onClick: e => {
          e.preventDefault();
          processAction(() => {
            logEvent('POSTER_DOWNLOADED', {
              id: posterId,
            });
            saveAs(pdfUrl, `${title}.pdf`);
          }, !state.isPrivateEvent && isAuthenticated);
        },
      };
    }

    return isAuthenticated
      ? {
          download: true,
          href: pdfUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick: () => {
            logEvent('POSTER_DOWNLOADED', {
              id: posterId,
            });
          },
        }
      : { disabled: true };
  };

  const DownloadPdfButton = () => (
    <a {...downloadProps()}>
      <Styles.DownloadButton color="primary" iconLeft={<FaDownload />}>
        Download
      </Styles.DownloadButton>
    </a>
  );

  const Buttons = (
    <Styles.HeroButtonContainer>
      <CopyToClipboard text={`https://doi.org/${doiLink}`}>
        <Styles.CopyDoiButton
          disabled={Boolean(!doiLink)}
          onClick={() => {
            toast('DOI copied to clipboard');
            logEvent('DOI_LINK_CLICKED');
          }}
          iconLeft={<FaCopy />}
        >
          Copy DOI
        </Styles.CopyDoiButton>
      </CopyToClipboard>
      {pdfUrl ? <DownloadPdfButton /> : null}
    </Styles.HeroButtonContainer>
  );

  return (
    <>
      <Styles.HeroTop hasScrollStarted={hasScrollStarted}>
        <Backbutton onClick={() => onBackButtonClick()}>
          <FaChevronLeft className={showBackText ? 'mr1' : ''} />
          {showBackText && 'All Submissions'}
        </Backbutton>

        {showTitle && (
          <Styles.StyledText
            style={{ marginLeft: '1rem', marginRight: '1rem' }}
            fontWeight={showBackText ? 'regular' : 'semiBold'}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <HTMLText>{title}</HTMLText>
          </Styles.StyledText>
        )}
        {isDesktop && Buttons}
      </Styles.HeroTop>
    </>
  );
};

TopSection.defaultProps = defaultProps;

export default TopSection;
